import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { PushTagOnClickDirective, TagEvent } from '@libs/shared-services';
import { AddTreatmentItem, CategoryListModule, InputComponent, SelectProviderComponent, TooltipModule } from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { DialogRef } from '@angular/cdk/dialog';
import { Store } from '@ngrx/store';
import { DataState, SiteFeatureFlag } from '@libs/utils';
import { selectUserSite } from '@libs/store';
import { ProgressNoteStatus, Provider } from '@ffd/api-client';
import { AddTreatmentModalService } from './add-treatment-modal.service';
import { TreatmentElementComponent } from './treatment-element';
import { SelectStatusComponent } from './select-status';
import { AddNewTreatmentData } from './add-treatment-modal.vm';
import { TreatmentTrayComponent } from './treatment-tray';

@Component({
  selector: 'ffd-add-treatment-modal',
  templateUrl: 'add-treatment-modal.component.html',
  styleUrls: ['add-treatment-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SvgIconComponent,
    TranslateModule,
    CategoryListModule,
    LottieComponent,
    TooltipModule,
    InputComponent,
    PushTagOnClickDirective,
    SelectProviderComponent,
    CommonModule,
    TreatmentElementComponent,
    SelectStatusComponent,
    TreatmentTrayComponent,
  ],
})
export class AddTreatmentModalComponent implements OnInit {
  private readonly dialogRef = inject(DialogRef);
  private readonly screenService = inject(AddTreatmentModalService);
  private readonly store = inject(Store);

  @ViewChild('applyButtonTooltip') public applyButtonTooltip!: TemplateRef<HTMLElement>;

  protected classifications = computed(() => this.screenService.viewModel().classifications);

  protected readonly DataState = DataState;
  protected readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  protected readonly TagEvent = TagEvent;

  protected selectedClassification = computed(() => this.screenService.viewModel().selectedClassification);
  protected searchQuery = computed(() => this.screenService.viewModel().searchQuery);
  protected selectedStatus = computed(() => this.screenService.viewModel().selectedStatus);
  protected currentProvider = computed(() => this.screenService.viewModel().currentProvider);
  protected addedTreatments = computed(() => this.screenService.viewModel().addedTreatments);

  protected readonly isAddTreatmentEnabled = computed(() => {
    return this.store
      .selectSignal(selectUserSite)()
      ?.featureFlags?.find((f) => f.featureFlag === SiteFeatureFlag.AddTreatment)?.isEnabled;
  });

  protected readonly applyButtonDisabled = computed(() => {
    return this.screenService.viewModel().addedTreatments.length === 0 || !this.isAddTreatmentEnabled();
  });

  public ngOnInit(): void {
    this.screenService.init();
  }

  protected onCloseClick(): void {
    this.screenService.resetTreatments();
    this.dialogRef.close();
  }

  protected onApplyClick(): void {
    this.screenService.setAddedTreatments(this.addedTreatments());
    this.dialogRef.close();
  }

  protected onSearchQueryChange(searchQuery: string): void {
    this.screenService.setSearchQuery(searchQuery);
  }

  protected onClassificationClick(classificationId: string): void {
    this.screenService.setSelectedClassificationId(classificationId);
  }

  protected onStatusChange(status: ProgressNoteStatus): void {
    this.screenService.setSelectedStatus(status);
  }

  protected onProviderChange(provider: Provider): void {
    this.screenService.setProvider(provider);
  }

  protected onAddTreatment(treatment: AddTreatmentItem): void {
    const treatmentWithStatus: AddNewTreatmentData = {
      ...treatment,
      status: treatment.isCondition ? ProgressNoteStatus.CON : this.selectedStatus(),
    };

    this.screenService.selectRequiredInput({ treatment: treatmentWithStatus });
  }
}
